import { IPersonBadge, IProduct, Nullable } from "../../types";
import {
  Badge,
  BadgeDownloader,
  DownloadFileTypes,
  MercuryUtils,
  PAPER_SIZE,
  QrCodeTypes,
  PDFEntitlementIcon
} from "@reedexpo/mercury-badge";
import { CountryItem, Regions } from "../../../redux/types";
import { ZPLBadge, fonts } from "./types";
import { BadgeDownloadOptions } from "../../../components/search/details/download/types";
import BadgeImageHelper from "../../helpers/badgeImage/BadgeImageHelper";
import utils from "../../../components/search/details/download/utils/utils";

type GenerateBadgeProps = {
  paperSize: PAPER_SIZE;
  type: DownloadFileTypes;
  product: IProduct;
  qrCode: string;
  regions: Array<Regions>;
  eventLocale: string;
  businessUnitCode: string;
  options: BadgeDownloadOptions;
};

export class BadgeBuilder {
  private readonly _badgeInfo: Nullable<IPersonBadge> = null;
  private _params: Object = {};
  private _generatedBadge?: Blob | ZPLBadge;
  private countryList: CountryItem[] = [];
  private _isFreya = false;

  constructor(badgeInfo: IPersonBadge, isFreya?: boolean) {
    this._badgeInfo = badgeInfo;
    this._isFreya = Boolean(isFreya);
  }

  get badgeInfo(): IPersonBadge {
    return this._badgeInfo!;
  }

  get params(): Object {
    return this._params;
  }

  get generatedBadge(): Blob | ZPLBadge {
    return this._generatedBadge!;
  }

  setCountryList(countries: CountryItem[]) {
    this.countryList = countries;
    return this;
  }

  async generateBadgeParams(regions: Array<Regions>, qrCode: string) {
    const parsedQrCodeContent = utils.parseQRCodeString(this._isFreya ? this.badgeInfo.qrCodeContent : qrCode, this.badgeInfo.customerType);
    const stateCode =
      regions.find(
        (region: Regions) => region.name === this.badgeInfo.organisation.state || region.code === this.badgeInfo.organisation.state
      )?.code || this.badgeInfo.organisation.state;
    this._params = {
      ...this.badgeInfo.contact,
      companyDisplayName: this.badgeInfo.organisation.displayName,
      standIds: this.badgeInfo.organisation.standIds,
      countryCode: this.badgeInfo.organisation.countryCode,
      stateCode,
      city: this.badgeInfo.organisation.city,
      badgeId: this.badgeInfo.id,
      badgeVersion: this._isFreya ? parsedQrCodeContent?.badgeVersion : this.badgeInfo.badgeVersion
    };
  }

  getFooterOptions(product: IProduct) {
    return {
      rxBadgeName: product.rx_badge_name,
      badgeName: product.name,
      badgeBarLetter: product.rx_badge_bar_letter,
      badgeBarFontColour: product.rx_badge_bar_font_colour,
      badgeBarBackgroundColour: product.rx_badge_bar_background_colour
    };
  }

  getBadgeIcons(product: IProduct) {
    let icons: PDFEntitlementIcon[] = [];
    try {
      icons = MercuryUtils.getBadgeIcons(MercuryUtils.getBadgeIconsOptions(product));
    } catch (e) {
      console.log(e);
    }
    return icons;
  }

  async generateBadge({ paperSize, type, product, qrCode, regions, eventLocale, businessUnitCode, options }: GenerateBadgeProps) {
    await this.generateBadgeParams(regions, qrCode);
    if (!product) {
      product = { sku: this.badgeInfo.productCode || "" } as IProduct;
    }
    let logo = "";
    let [image1, image2, image3]: string[] = ["", "", ""];

    if (!options.hideLogo) {
      logo = await BadgeImageHelper.getBadgeLogoForBadge(product.sku, this.badgeInfo, this._isFreya);
      [image1, image2, image3] = await BadgeImageHelper.getBadgeSideImagesForBadge(product.sku, this.badgeInfo, this._isFreya);
    }

    // @ts-ignore
    const pdfBlob = new Badge({ ...this.params });
    const commonProps = {
      fontsCulture: this.badgeInfo.culture,
      businessUnitCode,
      doubleSidedBadge: true,
      paperSize,
      badgeSuffix: product?.rx_badge_suffix,
      footerInfo: this.getFooterOptions(product),
      badgeIcons: this.getBadgeIcons(product),
      qrCode: { type: QrCodeTypes.BASE_64, src: qrCode },
      base64Images: { logo, image1, image2, image3 },
      sku: "",
      fonts
    };

    if (+type === DownloadFileTypes.ZPL) {
      this._generatedBadge = await pdfBlob.get(
        DownloadFileTypes.ZPL,
        {
          ...commonProps,
          // @ts-ignore
          paperSize: PAPER_SIZE.W105xH140mm,
          businessUnitCode,
          zplOptions: {
            width: 640 + 318,
            height: 1180,
            scale: 2.9
          }
        },
        eventLocale
      );
      return this;
    }

    this._generatedBadge = await pdfBlob.get(+type, { ...commonProps }, eventLocale);
    return this;
  }

  async download(fileName: string) {
    await BadgeDownloader.downloadBlobFile(this._generatedBadge as Blob, fileName);
  }
}
